/**
 * Реестр уже загруженных модулей.
 */
const loadedModulesRegistry = new Map<string, Promise<void>>();

const LOADER_ATTRIBUTE_NAME = 'javascript-resolver-module';

type OnScriptLoadHandler = () => void;
type OnScriptLoadErrorHandler = (error: ErrorEvent) => void;

const createScriptElement = (url: string): HTMLScriptElement => {
    const script = document.createElement("script");
    script.setAttribute("type", "text/javascript");
    script.setAttribute("async", "true");
    script.setAttribute("src", url);

    // Добавляем технический атрибут, чтобы по DOM-дереву можно было определить источник добавления.
    script.setAttribute(LOADER_ATTRIBUTE_NAME, 'true');

    return script;
}

interface LoadOptions {
    maxAttemptsCount?: number;
}

/**
 * Загружает JS-модули (не AMD) с поддержкой retry.
 *
 * @param  url URL скрипта для загрузки.
 * @param [options] Опции для загрузки скрипта.
 * @param [options.maxAttemptsCount=1] Максимальное количество попыток загрузки скрипта.
 * @returns Промис, который разрешается при успешной загрузке скрипта или отклоняется при ошибке.
 */
const load = (url: string, options: LoadOptions = {}): Promise<void> => {
    const {maxAttemptsCount = 1} = options;

    if (loadedModulesRegistry.has(url)) {
        return loadedModulesRegistry.get(url)!;
    }

    const attemptLoad = (attempt: number): Promise<void> => {
        return new Promise((resolve, reject) => {
            const script = createScriptElement(url);

            const unsubscribeFromEvents = () => {
                script.removeEventListener('load', onLoadHandler);
                script.removeEventListener('error', onErrorHandler);
            }

            const onLoadHandler: OnScriptLoadHandler = () => {
                unsubscribeFromEvents();
                resolve();
            };

            const onErrorHandler: OnScriptLoadErrorHandler = () => {
                unsubscribeFromEvents();
                document.head.removeChild(script);

                if (attempt < maxAttemptsCount) {
                    attemptLoad(attempt + 1).then(resolve).catch(reject);
                } else {
                    reject(
                        new Error(`Error while loading ${script.src}`)
                    );
                }
            };

            script.addEventListener('load', onLoadHandler);
            script.addEventListener('error', onErrorHandler);

            document.head.appendChild(script);
        });
    }

    const resolvedModule = attemptLoad(1);
    loadedModulesRegistry.set(url, resolvedModule);

    return resolvedModule;
}

export {
    load,
}
